import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceService } from '../../../services/api';
import { getAppMarket } from '../../../services/MarketService';
import { formatPropertyCity, formatPropertyLine1, formatPropertyPostcode, safeText } from '../../../utils/formatAddress';

interface PropertyCardProps {
    property: any;
    onDelete?: () => void;
    onClick?: (propertyId: string) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, onDelete, onClick }) => {
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const score = property.complianceScore;
    const isLoading = score === null;
    const isCompliant = score >= 80;
    const statusColor = isLoading ? 'gray' : (isCompliant ? 'green' : 'red');
    const statusText = isLoading ? 'Calculating...' : (isCompliant ? 'Compliant' : 'Action Required');

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!showDeleteConfirm) {
            setShowDeleteConfirm(true);
            return;
        }

        setIsDeleting(true);
        try {
            console.log('[PropertyCard] Deleting property:', property.id);
            const response = await complianceService.deleteProperty(property.id);
            console.log('[PropertyCard] Delete response:', response);

            if (onDelete) onDelete();

            // Show success message
            alert('Property deleted successfully!');
            window.location.reload(); // Refresh to show updated list
        } catch (error: any) {
            console.error('[PropertyCard] Delete error:', {
                error,
                response: error.response,
                message: error.message
            });

            const errorMessage = error.response?.data?.message
                || error.message
                || 'Failed to delete property. Please try again.';

            alert(`Delete failed: ${errorMessage}`);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'TBD';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div
            className="hp-compact-card group cursor-pointer"
            onClick={() => onClick ? onClick(property.id) : navigate(`/properties/${property.id}`)}
        >
            <div className="p-2 flex flex-col h-full bg-white">
                {/* Header: Address & Status */}
                <div className="flex justify-between items-start gap-1 mb-1.5">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 truncate uppercase tracking-tighter group-hover:text-emerald-500 transition-colors">
                            {formatPropertyLine1(property)}
                        </h3>
                        <p className="text-[9px] font-extrabold text-slate-400 -mt-1">
                            {formatPropertyPostcode(property)}
                            {formatPropertyCity(property) && ` • ${formatPropertyCity(property)}`}
                        </p>
                    </div>
                </div>

                {/* Info Row: Icons Only / Very Compact */}
                <div className="flex items-center gap-2 mb-2 px-1">
                    <div className={`text-[8px] font-black px-1 py-0.5 rounded shadow-sm whitespace-nowrap ${
                        isCompliant ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                    }`}>
                        {isCompliant ? 'PASS' : 'FAIL'}
                    </div>
                    <div className="flex items-center gap-1">
                        <i className="fas fa-bed text-slate-300 text-[8px]"></i>
                        <span className="text-[9px] font-bold text-slate-600">{safeText(property.bedrooms, '-')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <i className="fas fa-sterling-sign text-slate-300 text-[8px]"></i>
                        <span className="text-[9px] font-black text-slate-900">{safeText(property.rentAmount, '-')}</span>
                    </div>
                    {property.prsRegistrationNumber && (
                        <div className="flex items-center gap-1 ml-auto" title={getAppMarket() === 'texas' ? 'Rental Registered' : 'PRS Registered'}>
                            <i className="fas fa-clipboard-check text-indigo-300 text-[8px]"></i>
                            <span className="text-[8px] font-bold text-indigo-500 truncate max-w-[60px]">{safeText(property.prsRegistrationNumber)}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <i className="fas fa-building text-slate-300 text-[8px]"></i>
                        <span className="text-[9px] font-bold text-slate-500 uppercase truncate max-w-[40px]">{safeText(property.type, 'HMO')}</span>
                    </div>
                </div>

                {/* Compact Footer */}
                <div className="flex items-center justify-between mt-auto pt-1.5 border-t border-slate-50">
                    <div className="flex items-center gap-1">
                        <i className="fas fa-calendar-check text-slate-200 text-[8px]"></i>
                        <span className="text-[9px] font-black text-slate-400">{formatDate(property.nextInspection)}</span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={handleDelete}
                            className={`w-5 h-5 flex items-center justify-center rounded transition-all ${
                                showDeleteConfirm ? 'bg-rose-500 text-white' : 'text-slate-200 hover:text-rose-500 hover:bg-rose-50'
                            }`}
                        >
                            <i className={`fas ${isDeleting ? 'fa-spinner fa-spin' : showDeleteConfirm ? 'fa-check' : 'fa-trash-alt'} text-[8px]`}></i>
                        </button>
                        <i className="fas fa-chevron-right text-slate-200 text-[8px] group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all"></i>
                    </div>
                </div>
            </div>
        </div>


    );

};

export default PropertyCard;
